import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { isEmbedded } from "@/lib/tenant";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Clock, LogIn, LogOut, Coffee, ArrowLeft, MapPin, AlertCircle, CheckCircle, Loader2, User, Users, Key, Printer, Mail, UserPlus, Calendar, Edit3, Camera, X, TrendingUp, FileText, DollarSign, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PhotoAuditPane, { PunchPhotoThumb } from "../components/PhotoAuditPane";
import { WageUpLogo } from "../components/WageUpLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TimeCardPage = () => {
  const [employeeCode, setEmployeeCode] = useState("");
  const [employee, setEmployee] = useState(null);
  const [todayPunches, setTodayPunches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [punching, setPunching] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showMasterLogin, setShowMasterLogin] = useState(false);
  const [masterPassword, setMasterPassword] = useState("");
  const [masterLoggedIn, setMasterLoggedIn] = useState(false);
  const [payCatTab, setPayCatTab] = useState("cash"); // "cash" | "regular"
  const [masterName, setMasterName] = useState("");
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({ code: "", name: "", pay_type: "hourly", hourly_rate: 0, weekly_salary: 0, department: "Other", ot_eligible: true, pay_basis: "punches", pay_category: "payroll", pay_status: "active", is_manager: false });
  const [violations, setViolations] = useState([]);
  const [tenant, setTenant] = useState({ name: "Market Place on Oakton", tagline: "" });
  const locationCheckInterval = useRef(null);

  // Load tenant info once (multi-tenant branding)
  useEffect(() => {
    axios
      .get(`${API}/tenant/current`)
      .then((res) => {
        if (res.data?.exists) {
          setTenant({
            name: res.data.name || "PayPunch",
            tagline: res.data.tagline || "",
          });
        }
      })
      .catch(() => {});
  }, []);

  // Check location every 2 minutes while punched in
  useEffect(() => {
    const isPunchedIn = () => {
      if (todayPunches.length === 0) return false;
      const lastPunch = todayPunches[todayPunches.length - 1];
      return lastPunch.punch_type === "in" || lastPunch.punch_type === "lunch_in";
    };

    if (employee && isPunchedIn()) {
      // Start location monitoring
      locationCheckInterval.current = setInterval(async () => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(async (position) => {
            try {
              const response = await axios.post(`${API}/timeclock/location-check`, {
                employee_code: employeeCode,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
              if (response.data.status === "violation") {
                toast.error(`⚠️ You are ${response.data.distance_miles} miles from store!`, { duration: 10000 });
              }
            } catch (error) {
              console.error("Location check failed:", error);
            }
          });
        }
      }, 120000); // Check every 2 minutes

      return () => {
        if (locationCheckInterval.current) {
          clearInterval(locationCheckInterval.current);
        }
      };
    } else {
      // Stop monitoring if punched out
      if (locationCheckInterval.current) {
        clearInterval(locationCheckInterval.current);
      }
    }
  }, [employee, todayPunches, employeeCode]);

  const getCurrentLocation = () => {
    setGettingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
      },
      (error) => {
        let msg = "Unable to get location";
        if (error.code === 1) msg = "Location access denied. Please enable location in your browser settings.";
        else if (error.code === 2) msg = "Location unavailable";
        else if (error.code === 3) msg = "Location request timed out";
        setLocationError(msg);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const verifyEmployee = async () => {
    if (!employeeCode || employeeCode.length < 4) {
      toast.error("Please enter your code");
      return;
    }

    // Try master/manager unlock first — catches both full-master codes (83528/4817) AND manager codes (employees with is_manager=true).
    // If 401, fall through to regular employee punch-clock verify.
    try {
      const masterRes = await axios.post(`${API}/master/unlock`, { password: employeeCode });
      // HR-only users (like Eva) are redirected straight to /hr — they must not
      // see the timecard master dashboard.
      if (masterRes.data.hr_only) {
        toast.success(`HR mode — welcome, ${masterRes.data.master_name}`);
        window.location.assign(`/hr?pin=${encodeURIComponent(employeeCode)}`);
        return;
      }
      setMasterLoggedIn(true);
      setMasterTier(masterRes.data.tier || "master");
      setMasterName(masterRes.data.master_name || "");
      toast.success(masterRes.data.message || `Welcome, ${masterRes.data.master_name}`);
      loadMasterData();
      return;
    } catch (e) {
      // Not a master/manager code — continue to regular employee verify
    }

    // Regular employee login
    setLoading(true);
    try {
      const response = await axios.post(`${API}/timeclock/verify-employee`, { code: employeeCode });
      setEmployee(response.data.employee);
      setTodayPunches(response.data.today_punches || []);
      toast.success(`Welcome, ${response.data.employee.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Employee not found");
    } finally {
      setLoading(false);
    }
  };

  const handlePunch = async (punchType) => {
    if (!location) {
      toast.error("Location required. Please enable GPS.");
      getCurrentLocation();
      return;
    }

    setPunching(true);
    try {
      const response = await axios.post(`${API}/timeclock/punch`, {
        employee_code: employeeCode,
        punch_type: punchType,
        latitude: location.latitude,
        longitude: location.longitude
      });
      toast.success(response.data.message);
      setTodayPunches([...todayPunches, response.data.punch]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Punch failed");
    } finally {
      setPunching(false);
    }
  };

  const getLastPunchType = () => {
    if (todayPunches.length === 0) return null;
    return todayPunches[todayPunches.length - 1].punch_type;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const [masterTier, setMasterTier] = useState("master"); // "master" | "manager"

  const handleMasterLogin = async () => {
    try {
      // Use unified /master/unlock which returns tier (master vs manager vs hr)
      const response = await axios.post(`${API}/master/unlock`, { password: masterPassword });
      if (response.data.hr_only) {
        toast.success(`HR mode — welcome, ${response.data.master_name}`);
        window.location.assign(`/hr?pin=${encodeURIComponent(masterPassword)}`);
        return;
      }
      setMasterLoggedIn(true);
      setMasterTier(response.data.tier || "master");
      setMasterName(response.data.master_name || response.data.name || "");
      setShowMasterLogin(false);
      toast.success(response.data.message || `Welcome, ${response.data.master_name}!`);
      loadMasterData();
    } catch (error) {
      toast.error("Invalid master password");
    }
  };

  // True when current viewer is full master (sees everything)
  const isFullMaster = masterTier === "master";

  const [weekOffset, setWeekOffset] = useState(0);

  const loadMasterData = async (overrideOffset = null) => {
    const offset = overrideOffset !== null ? overrideOffset : weekOffset;
    try {
      const [empRes, reportRes, violationsRes] = await Promise.all([
        axios.get(`${API}/timeclock/employees/all`),
        axios.get(`${API}/timeclock/report/weekly`, { params: { week_offset: offset } }),
        axios.get(`${API}/timeclock/violations`)
      ]);
      setEmployees(empRes.data);
      setWeeklyReport(reportRes.data);
      setViolations(violationsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Reload report when week offset changes (after master logs in)
  useEffect(() => {
    if (masterLoggedIn) loadMasterData(weekOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, masterLoggedIn]);

  const addNewEmployee = async () => {
    if (!newEmployee.code || !newEmployee.name) {
      toast.error("Please enter code and name");
      return;
    }
    try {
      await axios.post(`${API}/timeclock/employees`, newEmployee);
      toast.success(`Employee ${newEmployee.name} added!`);
      setNewEmployee({ code: "", name: "", pay_type: "hourly", hourly_rate: 0, weekly_salary: 0, department: "Other", ot_eligible: true, pay_basis: "punches", pay_category: "payroll", pay_status: "active", is_manager: false });
      loadMasterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add employee");
    }
  };

  // Edit-employee modal state
  const [editingEmp, setEditingEmp] = useState(null);
  const saveEmployee = async () => {
    if (!editingEmp) return;
    try {
      await axios.put(`${API}/timeclock/employees/${editingEmp.code}`, {
        code: editingEmp.code,
        name: editingEmp.name,
        pay_type: editingEmp.pay_type || "hourly",
        hourly_rate: parseFloat(editingEmp.hourly_rate) || 0,
        weekly_salary: parseFloat(editingEmp.weekly_salary) || 0,
        department: editingEmp.department || "Other",
        ot_eligible: !!editingEmp.ot_eligible,
        pay_basis: editingEmp.pay_basis || "punches",
        pay_category: editingEmp.pay_category || "payroll",
        pay_status: editingEmp.pay_status || "active",
        is_manager: !!editingEmp.is_manager,
      });
      // hidden_from_schedule uses a dedicated PATCH endpoint
      await axios.patch(`${API}/timeclock/employees/${editingEmp.code}/hide-from-schedule?hidden=${!!editingEmp.hidden_from_schedule}`);
      toast.success("Employee updated");
      setEditingEmp(null);
      loadMasterData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const setEmployeePayStatus = async (emp, newStatus) => {
    try {
      await axios.put(`${API}/timeclock/employees/${emp.code}`, {
        ...emp,
        code: emp.code,
        name: emp.name,
        pay_type: emp.pay_type || "hourly",
        hourly_rate: parseFloat(emp.hourly_rate) || 0,
        weekly_salary: parseFloat(emp.weekly_salary) || 0,
        department: emp.department || "Other",
        ot_eligible: !!emp.ot_eligible,
        pay_basis: emp.pay_basis || "punches",
        pay_category: emp.pay_category || "payroll",
        pay_status: newStatus,
        is_manager: !!emp.is_manager,
      });
      toast.success(`${emp.name} → ${newStatus.replace("_"," ")}`);
      loadMasterData();
    } catch {
      toast.error("Failed to update pay status");
    }
  };

  const setEmployeeDepartment = async (emp, newDept) => {
    try {
      await axios.put(`${API}/timeclock/employees/${emp.code}`, {
        code: emp.code,
        name: emp.name,
        pay_type: emp.pay_type || "hourly",
        hourly_rate: parseFloat(emp.hourly_rate) || 0,
        weekly_salary: parseFloat(emp.weekly_salary) || 0,
        department: newDept,
        ot_eligible: !!emp.ot_eligible,
        pay_basis: emp.pay_basis || "punches",
        pay_category: emp.pay_category || "payroll",
        pay_status: emp.pay_status || "active",
        is_manager: !!emp.is_manager,
      });
      toast.success(`${emp.name} → ${newDept}`);
      loadMasterData();
    } catch {
      toast.error("Failed to update department");
    }
  };

  const toggleHideFromSchedule = async (emp) => {
    const newHidden = !emp.hidden_from_schedule;
    try {
      await axios.patch(`${API}/timeclock/employees/${emp.code}/hide-from-schedule?hidden=${newHidden}`);
      toast.success(`${emp.name} ${newHidden ? "hidden from" : "shown on"} schedule`);
      loadMasterData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const exportRoster = async () => {
    try {
      const res = await axios.get(`${API}/timeclock/employees/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `oakton-roster-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.count} employees`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const importRosterFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";  // reset
    if (!file) return;
    if (!window.confirm(`Import roster from "${file.name}"?\n\nYou will be asked whether to overwrite existing employees with the same code. Cancel if you're not sure.`)) return;
    const overwrite = window.confirm("Overwrite existing employees with same code? Click OK to OVERWRITE, Cancel to ONLY ADD NEW (safer).");
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const list = Array.isArray(data) ? data : (data.employees || []);
      if (!Array.isArray(list) || list.length === 0) {
        toast.error("Invalid JSON — no employees found");
        return;
      }
      const res = await axios.post(`${API}/timeclock/employees/import`, { employees: list, overwrite });
      toast.success(`✓ Added ${res.data.added}, updated ${res.data.updated}, skipped ${res.data.skipped} of ${res.data.total}`);
      loadMasterData();
    } catch (e) {
      toast.error("Import failed — " + (e.message || "bad file"));
    }
  };

  const deactivateEmployee = async (code, name) => {
    if (!window.confirm(`Deactivate ${name} TEMPORARILY? They won't appear in active lists but can be reactivated. All historical punches stay intact.`)) return;
    try {
      await axios.delete(`${API}/timeclock/employees/${code}`);
      toast.success(`${name} deactivated`);
      loadMasterData();
    } catch {
      toast.error("Failed to deactivate");
    }
  };

  const terminateEmployee = async (code, name) => {
    const reason = window.prompt(`TERMINATE ${name}? This marks them as no-longer-employed (different from temporary deactivation). Enter a reason (optional):`);
    if (reason === null) return; // cancelled
    try {
      await axios.post(`${API}/timeclock/employees/${code}/terminate`, { reason });
      toast.success(`${name} terminated`);
      loadMasterData();
    } catch {
      toast.error("Failed to terminate");
    }
  };

  const reactivateEmployee = async (code, name) => {
    try {
      await axios.post(`${API}/timeclock/employees/${code}/reactivate`);
      toast.success(`${name} reactivated`);
      loadMasterData();
    } catch {
      toast.error("Failed to reactivate");
    }
  };

  // Payslip state
  const [payslipPeriod, setPayslipPeriod] = useState("weekly"); // weekly, biweekly, monthly, custom
  const [payslipStart, setPayslipStart] = useState("");
  const [payslipEnd, setPayslipEnd] = useState("");
  const [payslipReport, setPayslipReport] = useState(null);

  const computePayslipDates = (period) => {
    const today = new Date();
    const day = today.getDay(); // 0=Sun..6=Sat
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    let start, end;
    if (period === "weekly") {
      start = new Date(monday);
      end = new Date(monday); end.setDate(monday.getDate() + 6);
    } else if (period === "biweekly") {
      start = new Date(monday); start.setDate(monday.getDate() - 7);
      end = new Date(monday); end.setDate(monday.getDate() + 6);
    } else { // monthly
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return { start: fmt(start), end: fmt(end) };
  };

  const loadPayslip = async (period) => {
    const { start, end } = period === "custom"
      ? { start: payslipStart, end: payslipEnd }
      : computePayslipDates(period);
    if (!start || !end) return toast.error("Pick start and end dates");
    setPayslipStart(start); setPayslipEnd(end); setPayslipPeriod(period);
    try {
      const res = await axios.get(`${API}/timeclock/report/range`, { params: { start, end } });
      setPayslipReport(res.data);
    } catch {
      toast.error("Failed to load payslip");
    }
  };

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [editingPunch, setEditingPunch] = useState(null);
  const [manualPunch, setManualPunch] = useState({ employee_code: "", punch_type: "in", timestamp: "" });
  const [activeTab, setActiveTab] = useState("weekly");

  const loadEmployeeHistory = async (code) => {
    try {
      const response = await axios.get(`${API}/timeclock/employee/${code}/history`);
      setEmployeeHistory(response.data);
      setSelectedEmployee(code);
    } catch (error) {
      toast.error("Failed to load history");
    }
  };

  const deletePunch = async (punchId) => {
    if (!confirm("Delete this punch?")) return;
    try {
      await axios.delete(`${API}/timeclock/punches/${punchId}`);
      toast.success("Punch deleted");
      loadEmployeeHistory(selectedEmployee);
      loadMasterData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const updatePunch = async (punchId, newTime) => {
    try {
      await axios.put(`${API}/timeclock/punches/${punchId}`, { timestamp: newTime });
      toast.success("Punch updated");
      setEditingPunch(null);
      loadEmployeeHistory(selectedEmployee);
      loadMasterData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const addManualPunch = async () => {
    if (!manualPunch.employee_code || !manualPunch.timestamp) {
      toast.error("Select employee and time");
      return;
    }
    try {
      await axios.post(`${API}/timeclock/punches/add`, manualPunch);
      toast.success("Manual punch added");
      setManualPunch({ employee_code: "", punch_type: "in", timestamp: "" });
      if (selectedEmployee === manualPunch.employee_code) {
        loadEmployeeHistory(selectedEmployee);
      }
      loadMasterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add");
    }
  };

  // Helper to format time from timestamp
  const formatPunchTime = (timestamp) => {
    if (!timestamp) return '—';
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }).toLowerCase().replace(' ', '');
    } catch {
      return '—';
    }
  };

  // Get punch summary for a day
  const getDayPunches = (punches) => {
    const result = { in: '—', out: '—', lunchOut: '—', lunchIn: '—', outAuto: false };
    if (!punches) return result;
    const firstIn = punches.find(p => p.type === 'in');
    if (firstIn) result.in = formatPunchTime(firstIn.time);
    const lastOut = [...punches].reverse().find(p => p.type === 'out');
    if (lastOut) {
      result.out = formatPunchTime(lastOut.time);
      result.outAuto = !!lastOut.auto_out;
    }
    const lunchOut = punches.find(p => p.type === 'lunch_out');
    if (lunchOut) result.lunchOut = formatPunchTime(lunchOut.time);
    const lunchIn = punches.find(p => p.type === 'lunch_in');
    if (lunchIn) result.lunchIn = formatPunchTime(lunchIn.time);
    return result;
  };

  // Build array of 7 date strings from weeklyReport (uses backend's Mon-Sun)
  const getWeekDates = () => {
    if (!weeklyReport?.start_date || !weeklyReport?.end_date) return [];
    const [sy, sm, sd] = weeklyReport.start_date.split('-').map(Number);
    const [ey, em, ed] = weeklyReport.end_date.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const dates = [];
    const cur = new Date(start);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  const formatDayHeader = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const dow = dt.toLocaleDateString('en-US', { weekday: 'short' });
    return { dow, md: `${m}/${d}` };
  };

  // Format decimal hours as "H:MM" (e.g., 7.53h → "7:32")
  const fmtHours = (h) => {
    if (!h || h <= 0) return "0:00";
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    if (mins === 60) return `${hrs + 1}:00`;
    return `${hrs}:${String(mins).padStart(2, '0')}`;
  };

  const getDayDetail = (emp, dateStr) => {
    return emp.daily_details?.find(d => d.date === dateStr) || null;
  };

  const sendWeeklyReport = async () => {
    try {
      const response = await axios.post(`${API}/timeclock/report/email`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send report");
    }
  };

  // Master Dashboard View
  if (masterLoggedIn) {
    const weekDates = getWeekDates();
    const allRows = weeklyReport?.report || [];
    const cashRows = allRows.filter((emp) => emp.pay_category === "cash");
    const regularRows = allRows.filter((emp) => emp.pay_category !== "cash");
    const reportRows = payCatTab === "cash" ? cashRows : regularRows;
    const totalViolations = violations?.summary?.length || 0;

    return (
      <div className="min-h-screen bg-[#1A1A1A] print:bg-white">
        <header className="bg-[#A61B1B] text-white py-2.5 px-3 print:hidden">
          <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-bold leading-tight">{isFullMaster ? "Master Dashboard" : "Manager Dashboard"}</h1>
              <p className="text-[11px] sm:text-xs text-white/80 truncate">Welcome, {masterName}{!isFullMaster && " · Limited view"}</p>
            </div>
            <Button
              onClick={() => { setMasterLoggedIn(false); setEmployee(null); }}
              variant="outline"
              size="sm"
              className="border-white text-white hover:bg-white hover:text-[#A61B1B] h-8 px-3 text-xs shrink-0"
              data-testid="btn-logout"
            >
              Logout
            </Button>
          </div>
        </header>

        {/* === MOBILE-FIRST QUICK ACTION GRID === */}
        <div className="bg-[#1A1A1A] px-3 py-3 print:hidden">
          <div className="max-w-7xl mx-auto">
            {/* HERO action: Snap POS report — front-and-center on phone */}
            <button
              onClick={() => window.location.href = "/weekly-report"}
              data-testid="btn-snap-pos-report"
              className="w-full bg-gradient-to-r from-[#A61B1B] to-[#841515] hover:from-[#841515] hover:to-[#6B1010] active:scale-[0.98] transition text-white rounded-2xl px-4 py-4 mb-3 shadow-lg flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <Camera className="w-7 h-7" />
                </div>
                <div className="text-left">
                  <div className="font-extrabold text-lg leading-tight">📸 Snap POS Report</div>
                  <div className="text-[11px] text-white/85 leading-tight mt-0.5">Take a photo or upload from gallery</div>
                </div>
              </div>
              <div className="text-white/70 text-xl">›</div>
            </button>

            {/* HERO action: WageUp Payroll Suite */}
            <button
              onClick={() => window.location.href = "/wageup"}
              data-testid="btn-open-wageup"
              className="w-full relative overflow-hidden bg-gradient-to-br from-[#3C0A0A] via-[#6B0F0F] to-[#1A1A1A] hover:from-[#4A0D0D] hover:via-[#7A1212] hover:to-[#2A2A2A] active:scale-[0.98] transition text-white rounded-2xl px-4 py-4 mb-3 shadow-xl border border-amber-500/20 flex items-center justify-between gap-3 group"
            >
              {/* Decorative arrow blur */}
              <span className="pointer-events-none absolute -right-6 -top-6 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl group-hover:bg-amber-400/30 transition" />
              <div className="flex items-center gap-3 relative">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0 ring-1 ring-white/20">
                  <WageUpLogo size={36} />
                </div>
                <div className="text-left">
                  <div className="font-extrabold text-lg leading-tight tracking-tight">WageUp <span className="text-amber-300 text-xs font-bold align-top ml-1">NEW</span></div>
                  <div className="text-[11px] text-white/80 leading-tight mt-0.5">Payroll · 1099 · Credentials Vault · Onboarding</div>
                  <div className="text-[9px] text-amber-200/80 leading-tight mt-0.5 uppercase tracking-widest">Powered by AV Tech</div>
                </div>
              </div>
              <div className="text-white/70 text-xl relative">›</div>
            </button>

            {/* Secondary nav grid — 2 cols mobile, 7 cols desktop */}
            <div className="grid grid-cols-2 sm:grid-cols-7 gap-2">
              <NavCard onClick={() => window.location.href = "/schedule"} icon={<Calendar className="w-5 h-5" />} label="Schedule" testid="btn-open-schedule" />
              <NavCard onClick={() => window.location.href = "/timesheet"} icon={<FileText className="w-5 h-5" />} label="Timesheet" testid="btn-open-timesheet" accent="green" />
              <NavCard onClick={() => window.location.href = "/hr"} icon={<Users className="w-5 h-5" />} label="HR" testid="btn-open-hr" accent="amber" />
              <NavCard onClick={() => window.location.href = "/metrics"} icon={<TrendingUp className="w-5 h-5" />} label="Metrics" testid="btn-open-metrics" />
              <NavCard onClick={() => window.location.href = "/kiosk"} icon={<Clock className="w-5 h-5" />} label="Kiosk Mode" testid="btn-launch-kiosk" />
              <NavCard onClick={() => window.location.href = "/kiosk-install"} icon={<Lock className="w-5 h-5" />} label="Set up iPad Kiosk" testid="btn-open-kiosk-install" accent="red" />
              <NavCard onClick={() => window.location.href = "/ai"} icon={<Sparkles className="w-5 h-5" />} label="AI Insights" testid="btn-open-ai" accent="amber" />
              <NavCard onClick={() => { setActiveTab("weekly"); setTimeout(() => window.print(), 200); }} icon={<Printer className="w-5 h-5" />} label="Print" testid="btn-print-timecard" />
              <NavCard onClick={sendWeeklyReport} icon={<Mail className="w-5 h-5" />} label="Email" testid="btn-email-report" />
            </div>
          </div>
        </div>

        {/* Print header (only visible when printing) */}
        <div className="hidden print:block px-4 py-3 border-b">
          <h1 className="text-xl font-bold">{tenant.name} — PayPunch Weekly Report</h1>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">PayPunch · Powered by AV Tech</p>
          {weeklyReport && <p className="text-sm text-gray-600">Period: {weeklyReport.period}</p>}
        </div>

        <div className="max-w-7xl mx-auto p-3 print:p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-[#2D2D2D] mb-3 print:hidden">
              <TabsTrigger value="weekly" data-testid="tab-weekly" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white text-gray-300 gap-1">
                <Calendar className="w-4 h-4" /> Weekly Hours
              </TabsTrigger>
              <TabsTrigger value="payslips" data-testid="tab-payslips" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white text-gray-300 gap-1">
                <Printer className="w-4 h-4" /> Payslips
              </TabsTrigger>
              <TabsTrigger value="violations" data-testid="tab-violations" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white text-gray-300 gap-1">
                <AlertCircle className="w-4 h-4" /> Violations
                {totalViolations > 0 && (
                  <span className="ml-1 bg-[#A61B1B] text-white text-[10px] rounded-full px-1.5 py-0.5">{totalViolations}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="employees" data-testid="tab-employees" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white text-gray-300 gap-1">
                <UserPlus className="w-4 h-4" /> Employees
              </TabsTrigger>
              <TabsTrigger value="edit" data-testid="tab-edit" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white text-gray-300 gap-1">
                <Edit3 className="w-4 h-4" /> Edit Punches
              </TabsTrigger>
              <TabsTrigger value="photos" data-testid="tab-photos" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white text-gray-300 gap-1">
                <Camera className="w-4 h-4" /> Photo Audit
              </TabsTrigger>
            </TabsList>

            {/* ============ WEEKLY HOURS TABLE ============ */}
            <TabsContent value="weekly" data-print-show className="mt-0">
              <div className="bg-white rounded-lg p-3 print:p-0 print:rounded-none">
                {weeklyReport && (
                  <div className="flex items-center justify-between mb-2 print:hidden">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setWeekOffset(weekOffset - 1)} data-testid="btn-prev-week" className="h-7">← Prev</Button>
                      <Button size="sm" variant="outline" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0} className="h-7">This Week</Button>
                      <Button size="sm" variant="outline" onClick={() => setWeekOffset(weekOffset + 1)} disabled={weekOffset >= 0} data-testid="btn-next-week" className="h-7">Next →</Button>
                      <span className="text-sm font-semibold ml-3 text-[#1A1A1A]">
                        {(() => {
                          const fmt = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${m}/${d}/${y}`; };
                          return `Mon ${fmt(weeklyReport.start_date)} → Sun ${fmt(weeklyReport.end_date)}`;
                        })()}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#666]">
                      <span className="text-[#1A5E1F]">IN</span> → <span className="text-[#A61B1B]">OUT</span> · <span className="text-[#92400E]">L:lunch</span> · <span className="text-[#A61B1B]">*</span> 30m auto-deducted (no lunch punched)
                    </p>
                  </div>
                )}

                <div className="flex gap-1 mb-3 print:hidden">
                  <button
                    onClick={() => setPayCatTab("cash")}
                    className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors border-b-2 ${
                      payCatTab === "cash"
                        ? "bg-white text-[#1A5E1F] border-[#1A5E1F]"
                        : "bg-gray-100 text-gray-500 border-transparent hover:bg-gray-200"
                    }`}
                    data-testid="paycat-tab-cash"
                  >
                    Cash ({cashRows.length})
                  </button>
                  <button
                    onClick={() => setPayCatTab("regular")}
                    className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors border-b-2 ${
                      payCatTab === "regular"
                        ? "bg-white text-[#1A5E1F] border-[#1A5E1F]"
                        : "bg-gray-100 text-gray-500 border-transparent hover:bg-gray-200"
                    }`}
                    data-testid="paycat-tab-regular"
                  >
                    Regular (W-2) ({regularRows.length})
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm print:text-[10px]" data-testid="weekly-timecard-table">
                    <thead>
                      <tr className="bg-[#1A1A1A] text-white print:bg-gray-200 print:text-black">
                        <th className="sticky left-0 bg-[#1A1A1A] print:bg-gray-200 px-3 py-3 text-left font-bold border border-[#333] min-w-[140px] text-base">Employee</th>
                        {weekDates.map((d) => {
                          const { dow, md } = formatDayHeader(d);
                          return (
                            <th key={d} className="px-2 py-3 text-center font-bold border border-[#333] min-w-[130px] text-base">
                              <div>{dow}</div>
                              <div className="text-xs font-normal opacity-80">{md}</div>
                            </th>
                          );
                        })}
                        <th className="px-3 py-3 text-center font-bold border border-[#333] bg-[#1A5E1F] print:bg-gray-300 min-w-[80px] text-base">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportRows.length === 0 && (
                        <tr>
                          <td colSpan={weekDates.length + 2} className="text-center py-6 text-[#666]">
                            No data for this period
                          </td>
                        </tr>
                      )}
                      {reportRows
                        .filter(emp => isFullMaster || emp.department !== "Admin" || emp.employee_code === "7861")
                        .map((emp) => (
                        <tr key={emp.employee_code} className="hover:bg-gray-50">
                          <td className="sticky left-0 bg-white px-3 py-2 font-bold border border-gray-200 text-[#1A1A1A] text-base">
                            <div>{emp.employee_name}</div>
                            <div className="text-xs font-normal text-[#666]">#{emp.employee_code}</div>
                          </td>
                          {weekDates.map((dateStr) => {
                            const day = getDayDetail(emp, dateStr);
                            const hasPunches = day && day.punches && day.punches.length > 0;
                            const anomalies = day?.anomalies || [];
                            const hasAnomaly = anomalies.length > 0;

                            if (!hasPunches) {
                              return (
                                <td key={dateStr} className="px-2 py-2 text-center border border-gray-200 text-gray-300 text-xl">
                                  —
                                </td>
                              );
                            }
                            const t = getDayPunches(day.punches);
                            const hasLunch = t.lunchOut !== '—' || t.lunchIn !== '—';
                            const cellBg = hasAnomaly ? "bg-[#FEF3C7]" : "";
                            const anomalyTip = anomalies.map(a => ({
                              missing_punch_out: "Missing punch OUT",
                              missing_punch_in: "Missing punch IN",
                              missing_lunch_in: "Did not return from lunch",
                              orphan_lunch_in: "Lunch back without lunch out",
                              no_lunch_punch: "No lunch punched — 30 min deducted",
                              auto_out_8pm: "Auto punched out at 8:00 PM (forgot to clock out)",
                            }[a] || a)).join(" · ");

                            return (
                              <td
                                key={dateStr}
                                className={`px-2 py-2 text-center border border-gray-200 align-middle ${cellBg}`}
                                title={anomalyTip || undefined}
                                data-testid={`cell-${emp.employee_code}-${dateStr}`}
                              >
                                <div className="leading-tight relative">
                                  {hasAnomaly && (
                                    <div className="absolute -top-1 -right-1 text-[#A61B1B]" title={anomalyTip}>
                                      <AlertCircle className="w-4 h-4" />
                                    </div>
                                  )}
                                  <div className="flex items-center justify-center gap-1 text-sm font-semibold">
                                    <span className="text-[#1A5E1F]" title="Punch In">{t.in}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className={`${t.out === '—' ? 'text-[#A61B1B] bg-[#FEE2E2] px-1 rounded' : (t.outAuto ? 'text-[#92400E] bg-[#FEF3C7] px-1 rounded' : 'text-[#A61B1B]')}`} title={t.out === '—' ? "MISSING punch out" : (t.outAuto ? "Auto-punched out at 8 PM (forgot to clock out)" : "Punch Out")}>
                                      {t.out === '—' ? '⚠ no out' : (t.outAuto ? `${t.out} ⏰` : t.out)}
                                    </span>
                                  </div>
                                  {hasLunch && (
                                    <div className="flex items-center justify-center gap-1 text-xs text-[#92400E] mt-0.5">
                                      <span title="Lunch Out">L:{t.lunchOut}</span>
                                      <span className="text-gray-400">→</span>
                                      <span className={t.lunchIn === '—' ? 'bg-[#FEE2E2] px-1 rounded text-[#A61B1B]' : ''} title={t.lunchIn === '—' ? "Did not return from lunch" : "Lunch Back"}>
                                        {t.lunchIn === '—' ? '⚠ no back' : t.lunchIn}
                                      </span>
                                    </div>
                                  )}
                                  <div className="font-extrabold text-[#1A1A1A] mt-1 text-2xl">
                                    {day.hours > 0 ? fmtHours(day.hours) : (hasAnomaly ? '—' : '0:00')}
                                    {day.lunch_deducted && <span className="text-[#A61B1B] text-base align-top" title="30 min auto-deducted (no lunch punched)">*</span>}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-center border border-gray-200 bg-[#F0FDF4] print:bg-gray-100 font-extrabold text-[#1A5E1F] text-2xl">
                            {fmtHours(emp.total_hours)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#F9FAFB] font-bold">
                        <td className="sticky left-0 bg-[#F9FAFB] px-3 py-2 border border-gray-200 text-[#1A1A1A] text-base">Daily Total</td>
                        {weekDates.map((dateStr) => {
                          const dayTotal = reportRows.reduce((sum, emp) => {
                            const day = getDayDetail(emp, dateStr);
                            return sum + (day?.hours || 0);
                          }, 0);
                          return (
                            <td key={dateStr} className="px-2 py-2 text-center border border-gray-200 text-[#1A1A1A] text-lg font-bold">
                              {dayTotal > 0 ? fmtHours(dayTotal) : '—'}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-center border border-gray-200 bg-[#1A5E1F] text-white text-2xl font-extrabold">
                          {fmtHours(reportRows.reduce((s, e) => s + (e.total_hours || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* ============ PAYSLIPS / PRINT ============ */}
            <TabsContent value="payslips" className="mt-0">
              <div className="bg-white rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-2 mb-4 print:hidden">
                  <span className="font-bold text-[#1A1A1A] text-sm">Pay period:</span>
                  <Button size="sm" onClick={() => loadPayslip("weekly")} className={payslipPeriod === "weekly" && payslipReport ? "bg-[#A61B1B]" : "bg-gray-200 text-[#1A1A1A] hover:bg-gray-300"} data-testid="btn-payslip-weekly">Weekly</Button>
                  <Button size="sm" onClick={() => loadPayslip("biweekly")} className={payslipPeriod === "biweekly" && payslipReport ? "bg-[#A61B1B]" : "bg-gray-200 text-[#1A1A1A] hover:bg-gray-300"} data-testid="btn-payslip-biweekly">Biweekly</Button>
                  <Button size="sm" onClick={() => loadPayslip("monthly")} className={payslipPeriod === "monthly" && payslipReport ? "bg-[#A61B1B]" : "bg-gray-200 text-[#1A1A1A] hover:bg-gray-300"} data-testid="btn-payslip-monthly">Monthly</Button>
                  <span className="text-sm text-[#666] ml-3">or custom:</span>
                  <Input type="date" value={payslipStart} onChange={(e) => setPayslipStart(e.target.value)} className="w-36 h-8 text-xs" />
                  <span>→</span>
                  <Input type="date" value={payslipEnd} onChange={(e) => setPayslipEnd(e.target.value)} className="w-36 h-8 text-xs" />
                  <Button size="sm" onClick={() => loadPayslip("custom")} className="bg-[#1A5E1F] h-8" data-testid="btn-payslip-load">Load</Button>
                  {payslipReport && (
                    <Button size="sm" onClick={() => window.print()} variant="outline" className="ml-auto h-8 gap-1" data-testid="btn-payslip-print">
                      <Printer className="w-4 h-4" /> Print
                    </Button>
                  )}
                </div>

                {!payslipReport ? (
                  <div className="text-center py-10 text-[#666] text-sm">Pick a period above to generate payslips.</div>
                ) : (
                  <div data-print-show data-testid="payslip-content">
                    <div className="border-b pb-2 mb-3">
                      <h2 className="text-xl font-bold text-[#1A1A1A]">{tenant.name} — Payroll Summary</h2>
                      <p className="text-sm text-[#666]">Pay period: <span className="font-semibold">{payslipReport.start_date} → {payslipReport.end_date}</span> (Chicago Time)</p>
                      {!isFullMaster && (
                        <div className="mt-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-2 py-1 rounded inline-block">
                          🔒 Manager view — Admin department individual pay is hidden. Cumulative totals shown.
                        </div>
                      )}
                    </div>

                    {(() => {
                      const fullReport = (payslipReport.report || []).filter(e => e.pay_category === "cash");
                      // Only Cash employees appear on Time Card — payroll/W2 employees are handled via Schedule + external payroll
                      const cashRows = fullReport;
                      const payrollRowsAll = []; // intentionally empty — timecard is for cash punch system only
                      // For manager tier: hide non-self Admin rows but keep their amounts in total
                      const payrollRowsVisible = isFullMaster
                        ? payrollRowsAll
                        : payrollRowsAll.filter(e => e.department !== "Admin" || e.employee_code === "7861");
                      const hiddenAdminRows = isFullMaster ? [] : payrollRowsAll.filter(e => e.department === "Admin" && e.employee_code !== "7861");
                      const hiddenAdminTotal = hiddenAdminRows.reduce((s, e) => s + (e.pay_amount || 0), 0);
                      const hiddenAdminHours = hiddenAdminRows.reduce((s, e) => s + (e.total_hours || 0), 0);
                      const grandTotalAll = fullReport.reduce((s, e) => s + (e.pay_amount || 0), 0);
                      const grandHoursAll = fullReport.reduce((s, e) => s + (e.total_hours || 0), 0);

                      const renderRow = (emp) => {
                        const flags = [];
                        emp.daily_details.forEach(d => { if (d.anomalies?.length) flags.push(...d.anomalies); });
                        const editedAny = emp.edited_punches > 0 || emp.manual_punches > 0;
                        const dimmed = emp.pay_status && emp.pay_status !== "active";
                        return (
                          <tr key={emp.employee_code} className={`border-b ${dimmed ? "opacity-50" : ""}`}>
                            <td className="px-2 py-1 font-semibold">
                              {emp.employee_name} <span className="text-[#666] font-normal">#{emp.employee_code}</span>
                              {emp.pay_status && emp.pay_status !== "active" && (
                                <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 px-1 rounded uppercase">{emp.pay_status.replace("_"," ")}</span>
                              )}
                              {emp.pay_category === "cash" && <span className="ml-1 text-[10px] bg-green-100 text-green-800 px-1 rounded">CASH</span>}
                            </td>
                            <td className="px-2 py-1 capitalize">{emp.pay_type}</td>
                            <td className="px-2 py-1 text-right">{emp.pay_type === "salaried" ? `$${emp.weekly_salary}/wk` : `$${emp.hourly_rate}/hr`}</td>
                            <td className="px-2 py-1 text-right font-bold">{fmtHours(emp.total_hours)}</td>
                            <td className="px-2 py-1 text-right font-bold text-[#1A5E1F]">${emp.pay_amount.toFixed(2)}</td>
                            <td className="px-2 py-1 text-center text-xs">
                              {flags.length > 0 && <span className="text-[#A61B1B] font-bold" title={[...new Set(flags)].join(', ')}>⚠ {flags.length}</span>}
                              {editedAny && <span className="text-[#3B82F6] ml-1" title={`${emp.edited_punches} edited / ${emp.manual_punches} manual`}>✎</span>}
                            </td>
                          </tr>
                        );
                      };

                      return (
                        <>
                          {/* Regular Payroll table */}
                          <div className="mb-1 mt-1 text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">Regular Payroll (paid via external payroll)</div>
                          <table className="w-full text-xs border-collapse mb-4">
                            <thead className="bg-[#1A1A1A] text-white">
                              <tr>
                                <th className="px-2 py-1 text-left">Employee</th>
                                <th className="px-2 py-1 text-left">Type</th>
                                <th className="px-2 py-1 text-right">Rate</th>
                                <th className="px-2 py-1 text-right">Hours</th>
                                <th className="px-2 py-1 text-right">Pay</th>
                                <th className="px-2 py-1 text-center">Flags</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payrollRowsVisible.map(renderRow)}
                              {hiddenAdminRows.length > 0 && (
                                <tr className="border-b bg-stone-50 italic text-stone-600">
                                  <td className="px-2 py-1">🔒 Admin (rolled up · {hiddenAdminRows.length} employee{hiddenAdminRows.length !== 1 ? "s" : ""})</td>
                                  <td className="px-2 py-1">—</td>
                                  <td className="px-2 py-1 text-right">—</td>
                                  <td className="px-2 py-1 text-right font-bold">{fmtHours(hiddenAdminHours)}</td>
                                  <td className="px-2 py-1 text-right font-bold">${hiddenAdminTotal.toFixed(2)}</td>
                                  <td></td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot>
                              <tr className="bg-gray-100 font-bold">
                                <td colSpan={3} className="px-2 py-1 text-right">Payroll Subtotal:</td>
                                <td className="px-2 py-1 text-right">{fmtHours(payrollRowsAll.reduce((s, e) => s + (e.total_hours || 0), 0))}</td>
                                <td className="px-2 py-1 text-right text-[#1A5E1F]">${payrollRowsAll.reduce((s, e) => s + (e.pay_amount || 0), 0).toFixed(2)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>

                          {/* Cash Payroll table */}
                          {cashRows.length > 0 && (
                            <>
                              <div className="mb-1 mt-2 text-xs font-bold text-green-800 uppercase tracking-wider">Cash Payroll (paid in cash · no OT)</div>
                              <table className="w-full text-xs border-collapse mb-4">
                                <thead className="bg-green-700 text-white">
                                  <tr>
                                    <th className="px-2 py-1 text-left">Employee</th>
                                    <th className="px-2 py-1 text-left">Type</th>
                                    <th className="px-2 py-1 text-right">Rate</th>
                                    <th className="px-2 py-1 text-right">Hours</th>
                                    <th className="px-2 py-1 text-right">Pay</th>
                                    <th className="px-2 py-1 text-center">Flags</th>
                                  </tr>
                                </thead>
                                <tbody>{cashRows.map(renderRow)}</tbody>
                                <tfoot>
                                  <tr className="bg-green-50 font-bold">
                                    <td colSpan={3} className="px-2 py-1 text-right">Cash Subtotal:</td>
                                    <td className="px-2 py-1 text-right">{fmtHours(cashRows.reduce((s,e)=>s+(e.total_hours||0),0))}</td>
                                    <td className="px-2 py-1 text-right text-green-700">${cashRows.reduce((s,e)=>s+(e.pay_amount||0),0).toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </>
                          )}

                          {/* Grand total */}
                          <div className="bg-[#1A1A1A] text-white px-3 py-2 rounded flex items-center justify-between mb-4">
                            <span className="text-sm font-bold uppercase tracking-wider">Grand Total (All Employees)</span>
                            <span className="text-lg font-black">${grandTotalAll.toFixed(2)} <span className="text-xs font-normal opacity-75">· {fmtHours(grandHoursAll)} hrs</span></span>
                          </div>
                        </>
                      );
                    })()}

                    {/* Per-employee detail (printable individual payslips) — cash only */}
                    <div className="space-y-4">
                      {payslipReport.report
                        .filter(emp => emp.pay_category === "cash")
                        .map((emp) => emp.daily_details.length > 0 && (
                        <div key={emp.employee_code} className="border rounded p-3 break-inside-avoid">
                          <div className="flex justify-between items-center mb-2 border-b pb-1">
                            <div>
                              <div className="font-bold text-[#1A1A1A]">{emp.employee_name} <span className="text-xs text-[#666] font-normal">#{emp.employee_code}</span></div>
                              <div className="text-xs text-[#666] capitalize">{emp.pay_type} · {emp.pay_type === "salaried" ? `$${emp.weekly_salary}/week` : `$${emp.hourly_rate}/hr`}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-[#666]">Hours</div>
                              <div className="font-bold text-[#1A1A1A]">{fmtHours(emp.total_hours)}</div>
                              <div className="text-xs text-[#666] mt-1">Pay</div>
                              <div className="font-bold text-[#1A5E1F]">${emp.pay_amount.toFixed(2)}</div>
                            </div>
                          </div>
                          <table className="w-full text-[11px]">
                            <thead className="text-[#666]">
                              <tr><th className="text-left py-0.5">Date</th><th className="text-left">In</th><th className="text-left">Lunch Out</th><th className="text-left">Lunch Back</th><th className="text-left">Out</th><th className="text-right">Hours</th><th className="text-left">Warnings</th></tr>
                            </thead>
                            <tbody>
                              {emp.daily_details.map((d) => {
                                const t = getDayPunches(d.punches);
                                const warns = (d.anomalies || []).map(a => ({
                                  missing_punch_out: "Missed punch out",
                                  missing_punch_in: "Missed punch in",
                                  missing_lunch_in: "Did not return from lunch",
                                  orphan_lunch_in: "Lunch back w/o lunch out",
                                  no_lunch_punch: "No lunch punched — 30 min deducted",
                                }[a] || a));
                                return (
                                  <tr key={d.date} className="border-t">
                                    <td className="py-0.5">{d.date}</td>
                                    <td>{t.in}</td>
                                    <td>{t.lunchOut}</td>
                                    <td>{t.lunchIn}</td>
                                    <td>{t.out}</td>
                                    <td className="text-right font-medium">{fmtHours(d.hours)}{d.lunch_deducted && <span className="text-[#A61B1B]">*</span>}</td>
                                    <td className="text-[10px] text-[#A61B1B]">{warns.join(" · ")}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {(emp.edited_punches > 0 || emp.manual_punches > 0) && (
                            <div className="text-[10px] text-[#3B82F6] mt-1">✎ {emp.edited_punches} edited / {emp.manual_punches} manually-added punch{(emp.edited_punches + emp.manual_punches) === 1 ? "" : "es"} during this period.</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ============ VIOLATIONS ============ */}
            <TabsContent value="violations" className="mt-0">
              <div className="bg-white rounded-lg p-4">
                <h2 className="text-base font-bold text-[#A61B1B] mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Location Violations (Last 7 Days)
                </h2>
                {(!violations?.summary || violations.summary.length === 0) ? (
                  <p className="text-sm text-[#666] py-4 text-center">No violations recorded</p>
                ) : (
                  <div className="space-y-2">
                    {violations.summary.map((v, idx) => (
                      <div key={idx} className="bg-[#FEE2E2] p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-bold text-[#991B1B] text-sm">{v.employee_name}</span>
                            <span className="text-xs text-[#7F1D1D] ml-2">({v.date})</span>
                          </div>
                          <span className="bg-[#A61B1B] text-white px-2 py-0.5 rounded-full text-xs font-bold">
                            {v.count}x
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-[#7F1D1D]">
                          {v.violations.slice(0, 4).map((viol, i) => (
                            <span key={i} className="mr-3 inline-block">
                              {new Date(viol.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} ({viol.distance_miles}mi)
                            </span>
                          ))}
                          {v.violations.length > 4 && <span>+{v.violations.length - 4} more</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ============ EMPLOYEES ============ */}
            <TabsContent value="employees" className="mt-0">
              {/* Export / Import Roster strip */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-amber-900 text-sm">📦 Migrate Roster Between Environments</div>
                  <div className="text-xs text-amber-700">Export this database's full employee list, then import on the other environment (preview ↔ production) so they stay in sync.</div>
                </div>
                <Button size="sm" onClick={exportRoster} className="bg-amber-700 hover:bg-amber-800 text-white gap-1" data-testid="btn-export-roster">⬇ Export ({employees.length})</Button>
                <input type="file" accept="application/json,.json" id="import-roster-input" style={{ display: "none" }} onChange={importRosterFile} />
                <Button size="sm" variant="outline" onClick={() => document.getElementById("import-roster-input").click()} className="border-amber-700 text-amber-700 hover:bg-amber-100 gap-1" data-testid="btn-import-roster">⬆ Import JSON</Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-4">
                  <h2 className="text-base font-bold text-[#1A1A1A] mb-3">Add Employee</h2>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Code (e.g., 7865)"
                        value={newEmployee.code}
                        onChange={(e) => setNewEmployee({...newEmployee, code: e.target.value})}
                        className="w-28"
                        data-testid="input-new-emp-code"
                      />
                      <Input
                        placeholder="Full name"
                        value={newEmployee.name}
                        onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                        className="flex-1"
                        data-testid="input-new-emp-name"
                      />
                    </div>
                    <div className="flex gap-3 text-sm">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" checked={newEmployee.pay_type === "hourly"} onChange={() => setNewEmployee({...newEmployee, pay_type: "hourly"})} data-testid="radio-hourly" />
                        Hourly
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" checked={newEmployee.pay_type === "salaried"} onChange={() => setNewEmployee({...newEmployee, pay_type: "salaried"})} data-testid="radio-salaried" />
                        Salaried
                      </label>
                    </div>
                    {newEmployee.pay_type === "hourly" ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#666]">$ per hour:</span>
                          <Input type="number" step="0.01" placeholder="e.g., 18.50" value={newEmployee.hourly_rate || ""} onChange={(e) => setNewEmployee({...newEmployee, hourly_rate: e.target.value})} className="w-32" data-testid="input-hourly-rate" />
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={newEmployee.ot_eligible} onChange={(e) => setNewEmployee({...newEmployee, ot_eligible: e.target.checked})} data-testid="check-ot" />
                          <span>Overtime eligible (1.5× rate after 40 hrs/week)</span>
                        </label>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#666]">Weekly salary $:</span>
                        <Input type="number" step="0.01" placeholder="e.g., 1200" value={newEmployee.weekly_salary || ""} onChange={(e) => setNewEmployee({...newEmployee, weekly_salary: e.target.value})} className="w-32" data-testid="input-weekly-salary" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-[#666]">Department:</span>
                      <select value={newEmployee.department} onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})} className="border rounded p-1.5 text-sm flex-1" data-testid="select-department">
                        {["Admin","Meat & Fish","Grocery","Produce","Deli","Cashier","Frozen/Dairy","Other"].map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="text-xs text-[#666]">Pay basis:</div>
                    <div className="flex gap-3 text-sm">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" checked={newEmployee.pay_basis === "punches"} onChange={() => setNewEmployee({...newEmployee, pay_basis: "punches"})} data-testid="radio-pay-punches" />
                        Punches (uses time clock)
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" checked={newEmployee.pay_basis === "schedule"} onChange={() => setNewEmployee({...newEmployee, pay_basis: "schedule"})} data-testid="radio-pay-schedule" />
                        Schedule (no time clock)
                      </label>
                    </div>
                    <div>
                      <label className="text-xs text-[#666]">Pay Category</label>
                      <div className="flex gap-2 text-xs mt-1">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={(newEmployee.pay_category || "payroll") === "payroll"} onChange={() => setNewEmployee({...newEmployee, pay_category: "payroll"})} data-testid="new-cat-payroll" />
                          <b>Payroll</b> (external · OT)
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" checked={newEmployee.pay_category === "cash"} onChange={() => setNewEmployee({...newEmployee, pay_category: "cash", pay_basis: "punches", ot_eligible: false})} data-testid="new-cat-cash" />
                          <b>Cash</b> (no OT · punches)
                        </label>
                      </div>
                    </div>
                    <Button onClick={addNewEmployee} className="bg-[#1A5E1F] w-full" data-testid="btn-add-employee">Add Employee</Button>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h2 className="text-base font-bold text-[#1A1A1A] mb-3">
                    Active Employees ({employees.filter(e => e.active).length})
                  </h2>
                  <div className="space-y-2 max-h-[36rem] overflow-y-auto pr-1">
                    {employees.filter(e => e.active).map((emp) => (
                      <div key={emp.code} className="bg-stone-50 hover:bg-stone-100 transition rounded-lg border border-stone-200 px-3 py-2.5" data-testid={`emp-card-${emp.code}`}>
                        {/* Row 1: Name + code (left) | Action buttons (right) */}
                        <div className="flex justify-between items-center gap-3 mb-2">
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold text-[#1A1A1A] text-[15px] tracking-wide">{emp.name}</span>
                            <span className="text-xs text-stone-500 ml-2 font-mono">#{emp.code}</span>
                            {emp.pay_category === "cash" && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-bold">CASH</span>
                            )}
                            {emp.is_manager && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-bold">MGR</span>
                            )}
                            {emp.pay_status && emp.pay_status !== "active" && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-bold uppercase">{emp.pay_status.replace("_"," ")}</span>
                            )}
                            {emp.hidden_from_schedule && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 font-bold uppercase">Off Schedule</span>
                            )}
                          </div>
                          <div className="flex gap-1.5 items-center shrink-0">
                            <Button size="sm" variant="outline" className="h-8 text-xs px-2.5" onClick={() => setEditingEmp({...emp})} data-testid={`btn-edit-emp-${emp.code}`}>Edit Pay</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs px-2.5" onClick={() => loadEmployeeHistory(emp.code)} data-testid={`btn-view-history-${emp.code}`}>History</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs px-2 text-[#92400E] border-[#92400E]/40 hover:bg-[#92400E] hover:text-white" onClick={() => deactivateEmployee(emp.code, emp.name)} data-testid={`btn-deactivate-${emp.code}`} title="Pause temporarily">Pause</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs px-2 text-[#A61B1B] border-[#A61B1B]/40 hover:bg-[#A61B1B] hover:text-white" onClick={() => terminateEmployee(emp.code, emp.name)} data-testid={`btn-terminate-${emp.code}`}>Terminate</Button>
                          </div>
                        </div>
                        {/* Row 2: Quick controls — Department / Status / Schedule visibility / Pay summary */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-stone-600">
                          <div className="flex items-center gap-1.5">
                            <span className="text-stone-500">Department:</span>
                            <select
                              value={emp.department || "Other"}
                              onChange={(e) => setEmployeeDepartment(emp, e.target.value)}
                              className="h-7 text-xs border border-stone-300 rounded px-1.5 bg-white font-medium"
                              data-testid={`dept-${emp.code}`}
                              title="Change department"
                            >
                              {["Admin","Meat & Fish","Grocery","Produce","Deli","Cashier","Frozen/Dairy","Other"].map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-stone-500">Status:</span>
                            <select
                              value={emp.pay_status || "active"}
                              onChange={(e) => setEmployeePayStatus(emp, e.target.value)}
                              className="h-7 text-xs border border-stone-300 rounded px-1.5 bg-white font-medium"
                              data-testid={`pay-status-${emp.code}`}
                              title="Pay status"
                            >
                              <option value="active">Active</option>
                              <option value="on_leave">On Leave</option>
                              <option value="inactive">Inactive</option>
                              <option value="terminated">Terminated</option>
                            </select>
                          </div>
                          <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Hide from Schedule Builder (still appears in metrics/punches)">
                            <input
                              type="checkbox"
                              checked={!!emp.hidden_from_schedule}
                              onChange={() => toggleHideFromSchedule(emp)}
                              data-testid={`hide-sched-${emp.code}`}
                              className="h-3.5 w-3.5"
                            />
                            <span className="text-stone-700 font-medium">Off schedule</span>
                          </label>
                          <span className="ml-auto text-[11px] text-stone-500 font-mono">
                            {emp.pay_type === "salaried" ? `Sal $${emp.weekly_salary || 0}/wk` : `$${emp.hourly_rate || 0}/hr${emp.ot_eligible && emp.pay_category !== "cash" ? " · OT" : ""}`}
                            {" · "}{emp.pay_basis === "schedule" ? "Schedule pay" : "Punch pay"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {employees.filter(e => !e.active && !e.terminated).length > 0 && (
                    <>
                      <h2 className="text-sm font-bold text-[#92400E] mt-4 mb-2">
                        Inactive (Temporary) ({employees.filter(e => !e.active && !e.terminated).length})
                      </h2>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {employees.filter(e => !e.active && !e.terminated).map((emp) => (
                          <div key={emp.code} className="flex justify-between items-center px-2 py-1.5 bg-yellow-50 rounded text-sm">
                            <div>
                              <span className="font-medium text-[#666]">{emp.name}</span>
                              <span className="text-xs text-[#888] ml-2">#{emp.code}</span>
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900">PAUSED</span>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7 text-xs bg-[#1A5E1F]" onClick={() => reactivateEmployee(emp.code, emp.name)} data-testid={`btn-reactivate-${emp.code}`}>Reactivate</Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs text-[#A61B1B] border-[#A61B1B]" onClick={() => terminateEmployee(emp.code, emp.name)}>Terminate</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {employees.filter(e => e.terminated).length > 0 && (
                    <>
                      <h2 className="text-sm font-bold text-[#A61B1B] mt-4 mb-2">
                        Terminated ({employees.filter(e => e.terminated).length})
                      </h2>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {employees.filter(e => e.terminated).map((emp) => (
                          <div key={emp.code} className="flex justify-between items-center px-2 py-1.5 bg-red-50 rounded text-sm opacity-75">
                            <div>
                              <span className="font-medium text-[#666] line-through">{emp.name}</span>
                              <span className="text-xs text-[#888] ml-2">#{emp.code}</span>
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-200 text-red-900">TERMINATED</span>
                              {emp.termination_reason && <span className="ml-2 text-[10px] text-[#666] italic">"{emp.termination_reason}"</span>}
                            </div>
                            <Button size="sm" className="h-7 text-xs bg-[#1A5E1F]" onClick={() => reactivateEmployee(emp.code, emp.name)}>Rehire</Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ============ EDIT PUNCHES ============ */}
            <TabsContent value="edit" className="mt-0">
              <div className="bg-white rounded-lg p-4 mb-3">
                <h2 className="text-base font-bold text-[#1A1A1A] mb-3">Add Manual Punch</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <select
                    value={manualPunch.employee_code}
                    onChange={(e) => setManualPunch({...manualPunch, employee_code: e.target.value})}
                    className="border rounded p-2 text-sm"
                    data-testid="manual-emp-select"
                  >
                    <option value="">Select Employee</option>
                    {employees.filter(e => e.active).map(emp => (
                      <option key={emp.code} value={emp.code}>{emp.name}</option>
                    ))}
                  </select>
                  <select
                    value={manualPunch.punch_type}
                    onChange={(e) => setManualPunch({...manualPunch, punch_type: e.target.value})}
                    className="border rounded p-2 text-sm"
                    data-testid="manual-punch-type"
                  >
                    <option value="in">Punch In</option>
                    <option value="out">Punch Out</option>
                    <option value="lunch_out">Lunch Out</option>
                    <option value="lunch_in">Lunch Back</option>
                  </select>
                  <Input
                    type="datetime-local"
                    onChange={(e) => setManualPunch({...manualPunch, timestamp: new Date(e.target.value).toISOString()})}
                    className="text-sm"
                    data-testid="manual-punch-time"
                  />
                  <Button onClick={addManualPunch} className="bg-[#1A5E1F]" data-testid="btn-add-manual-punch">Add Punch</Button>
                </div>
              </div>

              {selectedEmployee ? (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h2 className="text-base font-bold text-[#1A1A1A]">
                      Punch History: {employees.find(e => e.code === selectedEmployee)?.name}
                    </h2>
                    <Button variant="outline" size="sm" onClick={() => setSelectedEmployee(null)}>Close</Button>
                  </div>
                  <div className="space-y-1.5 max-h-96 overflow-y-auto">
                    {employeeHistory.map((punch) => (
                      <div key={punch.punch_id} className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <PunchPhotoThumb punchId={punch.punch_id} />
                          <div>
                            <span className={`font-medium ${punch.punch_type.includes('in') ? 'text-[#1A5E1F]' : 'text-[#A61B1B]'}`}>
                              {punch.punch_type.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className="text-xs text-[#666] ml-2">
                              {new Date(punch.timestamp).toLocaleString()}
                            </span>
                            {punch.manual_entry && <span className="text-[10px] text-[#F59E0B] ml-2">(Manual)</span>}
                            {punch.edited && <span className="text-[10px] text-[#3B82F6] ml-2">(Edited)</span>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => {
                              const newTime = prompt("Enter new time (YYYY-MM-DDTHH:MM)", punch.timestamp.slice(0, 16));
                              if (newTime) updatePunch(punch.punch_id, new Date(newTime).toISOString());
                            }}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 text-xs text-[#A61B1B] border-[#A61B1B]"
                            onClick={() => deletePunch(punch.punch_id)}
                          >
                            Del
                          </Button>
                        </div>
                      </div>
                    ))}
                    {employeeHistory.length === 0 && (
                      <p className="text-[#666] text-center py-4 text-sm">No punches found</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 text-center text-sm text-[#666]">
                  Select an employee from the <b>Employees</b> tab to view/edit punch history.
                </div>
              )}
            </TabsContent>

            {/* ============ PHOTO AUDIT TAB ============ */}
            <TabsContent value="photos" className="mt-0">
              <PhotoAuditPane masterPassword={masterPassword} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Edit Employee Modal */}
        {editingEmp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-5 max-w-md w-full">
              <h3 className="font-bold text-lg mb-3">Edit Employee — #{editingEmp.code}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#666]">Name</label>
                  <Input value={editingEmp.name} onChange={(e) => setEditingEmp({...editingEmp, name: e.target.value})} />
                </div>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={editingEmp.pay_type === "hourly"} onChange={() => setEditingEmp({...editingEmp, pay_type: "hourly"})} />
                    Hourly
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" checked={editingEmp.pay_type === "salaried"} onChange={() => setEditingEmp({...editingEmp, pay_type: "salaried"})} />
                    Salaried
                  </label>
                </div>
                {editingEmp.pay_type === "hourly" ? (
                  <>
                    <div>
                      <label className="text-xs text-[#666]">Hourly rate ($)</label>
                      <Input type="number" step="0.01" value={editingEmp.hourly_rate || ""} onChange={(e) => setEditingEmp({...editingEmp, hourly_rate: e.target.value})} data-testid="edit-hourly-rate" />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={!!editingEmp.ot_eligible} onChange={(e) => setEditingEmp({...editingEmp, ot_eligible: e.target.checked})} />
                      <span>Overtime eligible (1.5× after 40h)</span>
                    </label>
                  </>
                ) : (
                  <div>
                    <label className="text-xs text-[#666]">Weekly salary ($)</label>
                    <Input type="number" step="0.01" value={editingEmp.weekly_salary || ""} onChange={(e) => setEditingEmp({...editingEmp, weekly_salary: e.target.value})} data-testid="edit-weekly-salary" />
                  </div>
                )}
                <div>
                  <label className="text-xs text-[#666]">Department</label>
                  <select value={editingEmp.department || "Other"} onChange={(e) => setEditingEmp({...editingEmp, department: e.target.value})} className="w-full border rounded p-2 text-sm">
                    {["Admin","Meat & Fish","Grocery","Produce","Deli","Cashier","Frozen/Dairy","Other"].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#666]">Pay basis</label>
                  <div className="flex gap-3 text-sm mt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={(editingEmp.pay_basis || "punches") === "punches"} onChange={() => setEditingEmp({...editingEmp, pay_basis: "punches"})} />
                      Punches
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={editingEmp.pay_basis === "schedule"} onChange={() => setEditingEmp({...editingEmp, pay_basis: "schedule"})} />
                      Schedule
                    </label>
                  </div>
                </div>
                <div className="border-t pt-3 mt-1">
                  <label className="text-xs text-[#666]">Pay category</label>
                  <div className="flex gap-3 text-sm mt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={(editingEmp.pay_category || "payroll") === "payroll"} onChange={() => setEditingEmp({...editingEmp, pay_category: "payroll"})} data-testid="edit-cat-payroll" />
                      <span><b>Payroll</b> <span className="text-stone-500 text-xs">(external · OT eligible)</span></span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={editingEmp.pay_category === "cash"} onChange={() => setEditingEmp({...editingEmp, pay_category: "cash", pay_basis: "punches", ot_eligible: false})} data-testid="edit-cat-cash" />
                      <span><b>Cash</b> <span className="text-stone-500 text-xs">(paid in cash · no OT · punches)</span></span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#666]">Pay status</label>
                  <select value={editingEmp.pay_status || "active"} onChange={(e) => setEditingEmp({...editingEmp, pay_status: e.target.value})} className="w-full border rounded p-2 text-sm" data-testid="edit-pay-status">
                    <option value="active">Active — calculate pay normally</option>
                    <option value="on_leave">On Leave — pay paused ($0)</option>
                    <option value="inactive">Inactive — pay paused ($0)</option>
                    <option value="terminated">Terminated — pay paused ($0)</option>
                  </select>
                </div>
                {isFullMaster && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!editingEmp.is_manager} onChange={(e) => setEditingEmp({...editingEmp, is_manager: e.target.checked})} data-testid="edit-is-manager" />
                    <span><b>Manager</b> — this employee's code grants limited master access (Admin pay hidden)</span>
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editingEmp.hidden_from_schedule} onChange={(e) => setEditingEmp({...editingEmp, hidden_from_schedule: e.target.checked})} data-testid="edit-hidden-from-schedule" />
                  <span><b>Off schedule</b> — hide from the Schedule Builder grid (e.g. drivers, on-call cooks). Punches & metrics still apply.</span>
                </label>
                <div className="flex gap-2 mt-4">
                  <Button onClick={saveEmployee} className="flex-1 bg-[#1A5E1F]" data-testid="btn-save-emp-edit">Save</Button>
                  <Button variant="outline" onClick={() => setEditingEmp(null)} className="flex-1">Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A1A] to-[#2D2D2D] flex flex-col">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-[#333] py-4 px-4">
        <div className="max-w-md mx-auto flex justify-between items-center">
          {isEmbedded() ? (
            <div className="w-6" />
          ) : (
            <Link to="/" className="text-white hover:text-[#D4AF37]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
          )}
          <h1 className="text-white font-bold text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" /> PayPunch
          </h1>
          <div className="w-6"></div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          {!employee ? (
            // Employee Login
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#1A5E1F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-[#1A1A1A]">PayPunch</h2>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#A61B1B] font-bold mt-1">Powered by AV Tech</p>
                <p className="text-[#666] mt-2 text-sm">Enter your code to continue</p>
              </div>

              {/* Location Status */}
              <div className={`mb-6 p-3 rounded-lg flex items-center gap-2 ${
                location ? 'bg-[#D1FAE5] text-[#065F46]' : 
                locationError ? 'bg-[#FEE2E2] text-[#991B1B]' : 
                'bg-[#FEF3C7] text-[#92400E]'
              }`}>
                {gettingLocation ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Getting location...</>
                ) : location ? (
                  <><MapPin className="w-4 h-4" /> Location verified</>
                ) : locationError ? (
                  <><AlertCircle className="w-4 h-4" /> {locationError}</>
                ) : (
                  <><MapPin className="w-4 h-4" /> Checking location...</>
                )}
              </div>

              <Input
                type="number"
                placeholder="Enter your code"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
                className="text-center text-2xl font-bold h-14 mb-4"
                onKeyPress={(e) => e.key === 'Enter' && verifyEmployee()}
              />

              <Button
                onClick={verifyEmployee}
                disabled={loading || !employeeCode}
                className="w-full bg-[#1A5E1F] hover:bg-[#155219] h-12 text-lg"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue"}
              </Button>
            </div>
          ) : (
            // Punch Clock
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-[#1A1A1A]">{employee.name}</h2>
                <p className="text-[#666]">Code: {employee.code}</p>
              </div>

              {/* Current Time */}
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-[#1A1A1A]">
                  {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </div>
                <div className="text-[#666]">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </div>
              </div>

              {/* Today's Punches */}
              {todayPunches.length > 0 && (
                <div className="mb-6 bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-sm text-[#666] mb-2">Today's Activity</h3>
                  <div className="space-y-1">
                    {todayPunches.map((punch, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className={`font-medium ${
                          punch.punch_type.includes('in') ? 'text-[#1A5E1F]' : 'text-[#A61B1B]'
                        }`}>
                          {punch.punch_type.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-[#666]">{formatTime(punch.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Punch Buttons */}
              <div className="grid grid-cols-2 gap-3">
                {/* Punch In */}
                <Button
                  onClick={() => handlePunch("in")}
                  disabled={punching || getLastPunchType() === "in" || getLastPunchType() === "lunch_out"}
                  className="h-20 bg-[#1A5E1F] hover:bg-[#155219] flex-col"
                >
                  <LogIn className="w-6 h-6 mb-1" />
                  <span>PUNCH IN</span>
                </Button>

                {/* Punch Out */}
                <Button
                  onClick={() => handlePunch("out")}
                  disabled={punching || !getLastPunchType() || getLastPunchType() === "out" || getLastPunchType() === "lunch_out"}
                  className="h-20 bg-[#A61B1B] hover:bg-[#8B1515] flex-col"
                >
                  <LogOut className="w-6 h-6 mb-1" />
                  <span>PUNCH OUT</span>
                </Button>

                {/* Lunch Out */}
                <Button
                  onClick={() => handlePunch("lunch_out")}
                  disabled={punching || getLastPunchType() !== "in"}
                  variant="outline"
                  className="h-16 border-2 border-[#F59E0B] text-[#92400E] hover:bg-[#FEF3C7] flex-col"
                >
                  <Coffee className="w-5 h-5 mb-1" />
                  <span className="text-sm">Lunch Out</span>
                </Button>

                {/* Lunch In */}
                <Button
                  onClick={() => handlePunch("lunch_in")}
                  disabled={punching || getLastPunchType() !== "lunch_out"}
                  variant="outline"
                  className="h-16 border-2 border-[#1A5E1F] text-[#1A5E1F] hover:bg-[#D1FAE5] flex-col"
                >
                  <Coffee className="w-5 h-5 mb-1" />
                  <span className="text-sm">Lunch Back</span>
                </Button>
              </div>

              {/* Location Warning */}
              {!location && (
                <div className="mt-4 bg-[#FEE2E2] text-[#991B1B] p-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Location required to punch. 
                  <button onClick={getCurrentLocation} className="underline font-medium">
                    Enable GPS
                  </button>
                </div>
              )}

              {/* Request Time Off */}
              <Button
                onClick={() => window.location.assign(`/request-off?code=${encodeURIComponent(employeeCode)}`)}
                variant="outline"
                className="w-full mt-3 bg-white text-[#1A5E1F] border-[#1A5E1F] hover:bg-[#f0fdf4] font-semibold"
                data-testid="btn-request-off"
              >
                Request Time Off
              </Button>

              {/* Change Employee */}
              <Button
                onClick={() => { setEmployee(null); setEmployeeCode(""); setTodayPunches([]); }}
                variant="ghost"
                className="w-full mt-2 text-[#666]"
              >
                Switch Employee
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-[#666] text-sm">{tenant.name}{tenant.tagline ? ` — ${tenant.tagline}` : ""}</p>
      </footer>
    </div>
  );
};

export default TimeCardPage;

// Mobile-first nav card used in the master dashboard quick-action grid
const NavCard = ({ onClick, icon, label, testid, accent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 transition border ${
      accent === "amber"
        ? "bg-gradient-to-br from-amber-400/25 to-amber-500/10 hover:from-amber-400/40 hover:to-amber-500/20 text-amber-100 border-amber-400/40"
        : accent === "red"
        ? "bg-gradient-to-br from-red-500/25 to-red-700/10 hover:from-red-500/40 hover:to-red-700/20 text-red-100 border-red-400/40"
        : "bg-white/10 hover:bg-white/20 active:bg-white/30 text-white border-white/15"
    }`}
  >
    <div className={accent === "amber" ? "text-amber-200" : accent === "red" ? "text-red-200" : "text-white/95"}>{icon}</div>
    <div className="text-[11px] font-bold uppercase tracking-wide">{label}</div>
  </button>
);
